import React from "react";
import cn from "classnames";
import { useSelector } from "react-redux";
import { Heading, List, Icon, Text, IconName } from "@telia/teddy";
import { SellingPointsList, trackingContext } from "../../../../../../models";
import { isUspVisibleInFlow } from "../../../../../../shared/uspFlow";
import { isRetailer } from "../../../../app/state/selectors";
import SubscriptionFeatures from "../SubscriptionFeatures/SubscriptionFeatures";
import KeySellingPoints from "../KeySellingPoints/KeySellingPoints";
import LinkList from "../LinkList/LinkList";
import NotabeneCard from "../NotabeneCard/NotabeneCard";
import PromoCardList from "../PromoCardList/PromoCardList";
import { USP_CARD_LAYOUTS, USP_CARD_VARIANTS, USP_LEGACY_CARD_PRESETS } from "./uspCardPresentation";
import "./UspList.less";

interface UspListProps extends SellingPointsList {
    trackingContext?: trackingContext;
}

const UspList: React.FC<UspListProps> = ({
    title,
    text,
    sellingPoints: allSellingPoints,
    variation,
    variant,
    layout,
    trackingContext,
}) => {
    const isNexus = useSelector(isRetailer);
    const sellingPoints = allSellingPoints?.filter((point) => isUspVisibleInFlow(point.flow, isNexus));

    const renderContent = () => {
        switch (variation) {
            case "small-white-cards-grid":
            case "small-purple-cards-grid":
            case "small-beige-cards-grid":
                return <SubscriptionFeatures items={sellingPoints} {...USP_LEGACY_CARD_PRESETS[variation]} />;

            case "cards-grid": {
                const resolvedVariant = USP_CARD_VARIANTS.find((value) => value === variant) ?? "beige";
                const resolvedLayout = USP_CARD_LAYOUTS.find((value) => value === layout) ?? "default";
                return <SubscriptionFeatures items={sellingPoints} variant={resolvedVariant} layout={resolvedLayout} />;
            }

            case "big-grey-icons-row":
                return <KeySellingPoints sellingPoints={sellingPoints} />;

            case "navigation-with-arrow-list":
                return <LinkList sellingPoints={sellingPoints} />;

            case "notabene-list":
                return sellingPoints?.map((point) => (
                    <NotabeneCard
                        key={point.heading}
                        heading={point.heading}
                        content={point.content}
                        icon={point.icon as IconName}
                    />
                ));

            case "receipt-promo-card":
                return <PromoCardList trackingContext={trackingContext} sellingPoints={sellingPoints} />;

            case "big-beige-icons-row":
            default:
                return (
                    <List layout="horizontal">
                        {sellingPoints?.map((point) => (
                            <List.Item key={point.heading}>
                                <List.Illustration variant="icon-beige">
                                    <Icon
                                        size="lg"
                                        name={point.icon as IconName}
                                        aria-hidden="true"
                                        focusable="false"
                                    />
                                </List.Illustration>
                                <List.Content>
                                    <Heading mb="100" as={point.headingTag ?? "h2"} variant="title-100">
                                        {point.heading}
                                    </Heading>
                                    {point.subtext && <Text variant="paragraph-100">{point.subtext}</Text>}
                                    {point.content && (
                                        <Text asChild variant="paragraph-100">
                                            <div dangerouslySetInnerHTML={{ __html: point.content }} />
                                        </Text>
                                    )}
                                </List.Content>
                            </List.Item>
                        ))}
                    </List>
                );
        }
    };

    return (
        <div className={cn("usp-list", variation)}>
            {(title || text) && (
                <div className="usp-list__heading">
                    {title && (
                        <Heading mb="100" as="h2" variant="title-300" className="usp-list__selling-points-heading">
                            {title}
                        </Heading>
                    )}
                    {text && <div dangerouslySetInnerHTML={{ __html: text }} />}
                </div>
            )}
            {renderContent()}
        </div>
    );
};

export default UspList;
